import React, { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Filter, ExternalLink } from 'lucide-react';
import GuestFunnelPanel from '@/components/admin/GuestFunnelPanel';

/**
 * Conversion-Funnel-Dashboard (Paket C, Juli 2026):
 * Signup → Verifiziert → Aktiviert (≥1 Rechnung) → Zahlend,
 * plus Wochen-Signups und W1-Retention. Datenquelle: eigene MongoDB.
 * Detailanalysen (Landing-Traffic, Replays): PostHog-Link unten.
 */
export default function ConversionFunnelPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/conversion-funnel?days=${days}`);
      setData(res.data);
    } catch (e) {
      console.error('Conversion-Funnel laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="py-8 text-center text-gray-500" data-testid="funnel-loading">Lade Conversion-Funnel…</div>;
  }
  if (!data) return null;

  const maxCount = Math.max(...data.funnel.map(s => s.count), 1);
  const stepColors = ['bg-slate-400', 'bg-teal-500', 'bg-emerald-500', 'bg-emerald-700'];

  return (
    <div className="space-y-6" data-testid="conversion-funnel-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-bold text-gray-900">Conversion-Funnel</h3>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
            data-testid="funnel-range-select"
          >
            <option value={30}>letzte 30 Tage</option>
            <option value={90}>letzte 90 Tage</option>
            <option value={0}>gesamt</option>
          </select>
        </div>
      </div>

      {/* Funnel-Balken */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {data.funnel.map((step, i) => (
            <div key={step.step} data-testid={`funnel-step-${i}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{step.step}</span>
                <span className="text-sm font-mono text-gray-900">
                  {step.count}
                  <span className="ml-2 text-xs text-gray-400">{step.rate}%</span>
                </span>
              </div>
              <div className="h-7 w-full rounded-md bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-md ${stepColors[i]} transition-all duration-500`}
                  style={{ width: `${Math.max((step.count / maxCount) * 100, step.count > 0 ? 2 : 0)}%` }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Wochen-Signups */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Registrierungen pro Woche (12 Wochen)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.weekly_signups}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip />
                <Bar dataKey="signups" name="Signups" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Retention */}
        <Card data-testid="retention-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">W1-Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-semibold font-mono text-emerald-600">{data.retention_w1.rate}%</p>
            <p className="text-xs text-gray-500 mt-1">
              {data.retention_w1.retained} von {data.retention_w1.cohort_size} Nutzern
            </p>
            <p className="text-[11px] text-gray-400 mt-3 leading-snug">{data.retention_w1.definition}</p>
          </CardContent>
        </Card>
      </div>

      <a
        href="https://eu.posthog.com"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline"
        data-testid="posthog-link"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Landing-Traffic, Session Replays &amp; A/B-Tests in PostHog öffnen
      </a>

      {/* Gast-Trichter (Offener Einstieg) */}
      <div className="pt-4 border-t border-slate-100">
        <GuestFunnelPanel />
      </div>
    </div>
  );
}
